import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useInterval from "../hooks/useInterval";
import { getCookieConsentValue } from "react-cookie-consent";

const defaultOptions = {
  property: "Corporate",
};

export function isHeap(heap) {
  return heap?.identify !== undefined && heap?.loaded;
}

let isModalCloseEvent = false;
const pageviewQueue = [];

// Process any pageviews that tried to be tracked before the scripts were finished loading
const processQueue = () => {
  while (pageviewQueue.length) {
    const pageview = pageviewQueue.pop();
    trackHeapPageView(pageview);
  }
};

const trackHeapPageView = (options) => {
  if (getCookieConsentValue() !== "true") {
    console.warn("Trying to track heap page view but cookie consent is false");
    return;
  }

  if (!isHeap(window.heap)) {
    console.warn(
      "Attempting to track page view but heap has not finished loading. Adding to the queue for future processing."
    );
    pageviewQueue.push(options);
    return;
  }

  const mergedOptions = Object.assign({}, defaultOptions, options ?? {});

  if (!mergedOptions.property) mergedOptions.property = defaultOptions.property;

  try {
    window.heap.track("Page View", mergedOptions);
  }
  catch (ex) {
    console.warn("Error calling heap.track: ", ex.message);
  }
};

const HeapAnalytics = ({heapAppId, cookiesAccepted}) => {
  const history = useHistory();
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [ready, setReady] = useState(false);
  const [isInitialPageView, setInitialPageView] = useState(true);
  const delay = 1000;

  // This will be called only when the user consents to cookies
  const initHeapAnalytics = (heapAppId) => {
    if(!heapAppId) {
      console.warn("No Heap ID")
      return
    }
    const heapScript = document.querySelector('#heap-script');

    if (heapScript) {
      console.warn("Heap script already loaded");
      return;
    }

    var s = document.createElement('script');
    s.type = 'text/javascript';
    s.id = "heap-script"
    var code = `
      window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
      heap.load(${heapAppId});
    `
    try {
      s.appendChild(document.createTextNode(code));
      document.body.appendChild(s);
    } catch (e) {
      s.text = code;
      document.body.appendChild(s);
    }

    setCount(0);
  }

  // Poll to check if heap and further have finished loading
  const clearTheInterval = useInterval(() => {
    setCount(count + 1);

    if (getCookieConsentValue() === "true") {
      if (
        isHeap(window.heap) &&
        window.heap.loaded &&
        window.FurtherSiteManager &&
        window.FurtherSiteManager.visitorUUID
      ) {
        onReady();
        clearTheInterval();
      }
    }

    if (count === 20) {
      console.warn("Heap not loaded after 20 seconds");
    }

    if (count === 30) {
      console.warn("Heap not loaded after 30 seconds");
      clearTheInterval();
    }
  }, delay);

  useEffect(() => {
    if (cookiesAccepted) {
      initHeapAnalytics(heapAppId);
    }
  }, [heapAppId, cookiesAccepted]);

  // Heap & Further have finished loading
  const onReady = () => {
    try {
      // Set heap identify with Further visitorUUID
    window.heap.identify(window.FurtherSiteManager.visitorUUID);
    }
    catch (ex) {
      console.warn("Error calling heap.identify: ", ex.message);
    }
    processQueue();
    setReady(true);
  };

  // Ready and initial pageview
  useEffect(() => {
    if (ready) {
      const heapDiv = document.querySelector('#heap-property');
      const isPropertyPage = location?.state?.isPropertyPage;

      // Set the value for the heap property back to Corporate if its not a property page
      if (heapDiv && !isPropertyPage) {
        heapDiv.setAttribute('data-heap-property', defaultOptions.property);
      }

      if (isInitialPageView) {
        // Initial pageview
        // Property pageviews will be tracked in PropertyPage.js

        if (!isPropertyPage) {
          trackHeapPageView();
        }

        // Listen for url changes
        history.listen((e) => {
          if (isModalCloseEvent) {
            // Don't track the pageview again when the modal is closed
            isModalCloseEvent = false;
            return;
          }
          // Modal page views will be tracked in Property.js
          if (!e?.state?.modal) {
            trackHeapPageView();
          } else {
            isModalCloseEvent = true;
          }
        });

        setInitialPageView(false);
      }
    }
  }, [ready, isInitialPageView, history, location]);
  return (
    <div
      id="heap-property"
      data-heap-property={defaultOptions.property}
      style={{display: "none"}}
    ></div>
  )
};

export default HeapAnalytics;

export { trackHeapPageView, defaultOptions as defaultHeapOptions };
